import React, { useCallback } from 'react';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Box, Flex, VStack } from '@televet/kibble-ui/build/chakra';
import { useWidgetStore } from 'state/useWidgetStore';
import { MembershipStatus } from 'state/types/teletriage';
import { ClinicPhoneOrEmailLinks } from 'shared/components/ClinicPhoneOrEmailLinks';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

interface MembershipStatusContent {
  alertTitle: string;
  description: JSX.Element | string;
}

const membershipStatusCopy: Record<MembershipStatus, MembershipStatusContent | null> = {
  hasNoPets: {
    alertTitle: 'No Pets Found',
    description:
      "We couldn't find a pet associated with your account. Please contact the clinic directly to add a pet to your account.",
  },
  hasNoMemberships: {
    alertTitle: 'No Membership Found',
    description:
      "We couldn't find a membership associated with your account. Use the button below to request a link to sign up for a membership now to start chatting with our 24/7 support staff!",
  },
  hasNoProfile: {
    alertTitle: 'No Profile Found',
    description:
      "We couldn't find your account. Try another phone number or email, or contact the clinic directly to become a client!",
  },
  isInactive: {
    alertTitle: 'No Profile Found',
    description:
      "We couldn't find your account. Try another phone number or email, or contact the clinic directly to become a client!",
  },
  isEnrolled: null,
  noTeletriage: {
    alertTitle: 'Live Chat is no longer being offered on new Care Membership plans',
    description: (
      <Box mt={4} mb={4}>
        <Flex mt={2}>
          <Icon name="phone" />
          <Text as="p" ml={2}>
            <Text fontWeight="semibold">For non-urgent requests: </Text>
            Reach out during regular business hours, and our team will be happy to assist you.
          </Text>
        </Flex>
        <Flex mt={2}>
          <Icon name="warningSign" />
          <Text as="p" ml={2}>
            <Text fontWeight="semibold">For urgent after hours requests: </Text>
            Please contact your nearest ER veterinary clinic directly.
          </Text>
        </Flex>
      </Box>
    ),
  },
};

interface InvalidMembershipStatusProps {
  sendSignUpLink: () => Promise<void>;
  isSendingSignUpLink?: boolean;
}

export function InvalidMembershipStatus({
  sendSignUpLink,
  isSendingSignUpLink,
}: InvalidMembershipStatusProps): JSX.Element {
  const {
    authentication: { emailAddress },
    updateTeletriageState,
    goBackToMainMenu,
    teletriage: { membershipStatus },
  } = useWidgetStore((state) => state);

  const handleButtonClick = useCallback(async () => {
    if (membershipStatus === 'hasNoMemberships') {
      updateTeletriageState({ linkSendMethod: emailAddress ? 'email' : 'phone number' });
      await sendSignUpLink();
    } else {
      goBackToMainMenu();
    }
  }, [emailAddress, goBackToMainMenu, membershipStatus, sendSignUpLink, updateTeletriageState]);

  if (!membershipStatus) {
    return <></>;
  }

  return (
    <VStack h="100%" p={4} m={6} spacing={5} align="flex-start" justify="">
      <Alert w="100%" title={membershipStatusCopy[membershipStatus]?.alertTitle} status="warning" hideCloseButton />
      {typeof membershipStatusCopy[membershipStatus]?.description === 'string' ? (
        <Text>{membershipStatusCopy[membershipStatus]?.description}</Text>
      ) : (
        membershipStatusCopy[membershipStatus]?.description
      )}
      {membershipStatus !== 'hasNoMemberships' ? <ClinicPhoneOrEmailLinks /> : <></>}
      <Button
        onClick={handleButtonClick}
        isFullWidth
        {...(membershipStatus === 'hasNoMemberships' && { iconName: 'send', isLoading: isSendingSignUpLink })}
      >
        {membershipStatus !== 'hasNoMemberships' ? 'Back to Options' : 'Send Me a Link'}
      </Button>
    </VStack>
  );
}
